import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiSettings,
  FiLayers,
  FiMapPin,
  FiDatabase,
  FiShield,
  FiGlobe,
  FiBell,
  FiMail
} from 'react-icons/fi';
import { AdminLayout } from '../../components/admin';
import toast from 'react-hot-toast';

interface SettingCard {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  path: string;
  badge?: string;
  color: string;
}

const AdminSettingsPage: React.FC = () => {
  const navigate = useNavigate();

  const masterDataSettings: SettingCard[] = [
    {
      id: 'service-types',
      title: 'ประเภทบริการ',
      description: 'จัดการประเภทบริการรักษาความปลอดภัย (Service Types)',
      icon: FiLayers,
      path: '/admin/service-types',
      badge: 'Master Data',
      color: 'blue'
    },
    {
      id: 'provinces',
      title: 'จังหวัด',
      description: 'จัดการข้อมูลจังหวัดและพื้นที่ (Provinces)',
      icon: FiMapPin,
      path: '/admin/provinces',
      badge: 'Master Data',
      color: 'green'
    }
  ];

  const systemSettings: SettingCard[] = [
    {
      id: 'database',
      title: 'ฐานข้อมูล',
      description: 'การตั้งค่าและบำรุงรักษาฐานข้อมูล',
      icon: FiDatabase,
      path: '#',
      color: 'purple'
    },
    {
      id: 'security',
      title: 'ความปลอดภัย',
      description: 'การตั้งค่าความปลอดภัยและการยืนยันตัวตน',
      icon: FiShield,
      path: '#',
      color: 'red'
    },
    {
      id: 'localization',
      title: 'ภาษาและท้องถิ่น',
      description: 'การตั้งค่าภาษา เขตเวลา และรูปแบบ',
      icon: FiGlobe,
      path: '#',
      color: 'indigo'
    },
    {
      id: 'notifications',
      title: 'การแจ้งเตือน',
      description: 'การตั้งค่าการแจ้งเตือนและการส่งข้อความ',
      icon: FiBell,
      path: '#',
      color: 'yellow'
    },
    {
      id: 'email',
      title: 'อีเมล',
      description: 'การตั้งค่า SMTP และเทมเพลตอีเมล',
      icon: FiMail,
      path: '#',
      color: 'pink'
    }
  ];

  const handleCardClick = (path: string, title: string) => {
    if (path !== '#') {
      navigate(path);
    } else {
      // Show toast for features in development
      toast(`ฟีเจอร์ "${title}" กำลังพัฒนา - เร็วๆ นี้`, {
        duration: 3000,
      });
    }
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      blue: {
        bg: 'bg-blue-50',
        text: 'text-blue-600',
        border: 'border-blue-200 hover:border-blue-400'
      },
      green: {
        bg: 'bg-green-50',
        text: 'text-green-600',
        border: 'border-green-200 hover:border-green-400'
      },
      purple: {
        bg: 'bg-purple-50',
        text: 'text-purple-600',
        border: 'border-purple-200 hover:border-purple-400'
      },
      red: {
        bg: 'bg-red-50',
        text: 'text-red-600',
        border: 'border-red-200 hover:border-red-400'
      },
      indigo: {
        bg: 'bg-indigo-50',
        text: 'text-indigo-600',
        border: 'border-indigo-200 hover:border-indigo-400'
      },
      yellow: {
        bg: 'bg-yellow-50',
        text: 'text-yellow-600',
        border: 'border-yellow-200 hover:border-yellow-400'
      },
      pink: {
        bg: 'bg-pink-50',
        text: 'text-pink-600',
        border: 'border-pink-200 hover:border-pink-400'
      }
    };
    return colors[color] || colors.blue;
  };

  const renderSettingCard = (setting: SettingCard) => {
    const colors = getColorClasses(setting.color);
    const IconComponent = setting.icon;
    const isDisabled = setting.path === '#';

    return (
      <motion.div
        key={setting.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div
          onClick={() => handleCardClick(setting.path, setting.title)}
          className={`
            relative p-6 bg-white border-2 rounded-xl transition-all duration-200
            ${colors.border}
            ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:shadow-lg'}
          `}
        >
          {/* Badge */}
          {setting.badge && (
            <div className="absolute top-4 right-4">
              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                {setting.badge}
              </span>
            </div>
          )}

          {/* Icon */}
          <div className={`inline-flex p-3 ${colors.bg} rounded-lg mb-4`}>
            <IconComponent className={`w-6 h-6 ${colors.text}`} />
          </div>

          {/* Content */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {setting.title}
          </h3>
          <p className="text-sm text-gray-600">
            {setting.description}
          </p>

          {/* Coming Soon */}
          {isDisabled && (
            <div className="mt-4">
              <span className="inline-flex items-center px-3 py-1 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
                เร็วๆ นี้
              </span>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-8">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-50 rounded-xl">
            <FiSettings className="w-7 h-7 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">ตั้งค่าระบบ</h1>
            <p className="text-sm text-gray-600 mt-1">
              จัดการการตั้งค่าและคอนฟิกระบบ Thai Security Directory
            </p>
          </div>
        </div>

        {/* Master Data Section */}
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <FiDatabase className="w-5 h-5 text-blue-600" />
              Master Data Management
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              จัดการข้อมูลหลักที่ใช้ทั่วทั้งระบบ
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {masterDataSettings.map(renderSettingCard)}
          </div>
        </div>

        {/* System Settings Section */}
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <FiSettings className="w-5 h-5 text-gray-600" />
              การตั้งค่าระบบ
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              การตั้งค่าและคอนฟิกระบบต่างๆ
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {systemSettings.map(renderSettingCard)}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiSettings className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">
                คำแนะนำ
              </h3>
              <p className="text-sm text-blue-700">
                การเปลี่ยนแปลงการตั้งค่า Master Data จะส่งผลกระทบต่อระบบทั้งหมด
                กรุณาตรวจสอบให้แน่ใจก่อนทำการแก้ไข
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
